import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Divider, Stack, Skeleton, Alert, Button,
  CircularProgress, Switch, FormControlLabel, TextField, Tooltip,
} from '@mui/material';
import { ENDPOINTS, apiCall } from '../utils/api.js';
import { GROUP_ORDER, groupLabel } from './messageTypes.js';

const cardSx = { p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' };

// Org notification settings: master switch + SMS/email channels (the parents), reminder lead
// times, and per-group enable + monthly cap for each of the 5 notification groups
// (booked / queue position / reminder / link sent / status changed). Plus per-category
// queue-position alerts. Backend: /organizations/{id}/notification-settings/.
export default function NotificationsCard({ orgId, refreshKey = 0 }) {
  const [enabled, setEnabled] = useState(false);
  const [viaSms, setViaSms] = useState(true);
  const [viaEmail, setViaEmail] = useState(false);
  // Whether the org's package permits SMS (Starter is email-only). Null package => allowed.
  const [smsAllowed, setSmsAllowed] = useState(true);
  const [packageName, setPackageName] = useState('');
  // True when the caller can't edit the per-group caps (org admin on a free trial — sqip manages them).
  const [limitsLocked, setLimitsLocked] = useState(false);
  // Provisioning limits (sqip-admin only): org overrides ('' = use plan default) + current usage.
  const [canEditProvisioning, setCanEditProvisioning] = useState(false);
  const [maxCategories, setMaxCategories] = useState('');
  const [maxConfigUsers, setMaxConfigUsers] = useState('');
  const [usage, setUsage] = useState({ categories: 0, users: 0, effCats: null, effUsers: null });
  const [remDays, setRemDays] = useState('');
  // Per-group settings: { [group]: { enabled: bool, cap: string } }
  const [groups, setGroups] = useState({});
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!orgId) return undefined;
    let alive = true;
    const id = setTimeout(async () => {   // deferred so we don't setState synchronously in the effect
      setLoading(true);
      const [s0, c] = await Promise.all([
        apiCall(ENDPOINTS.ORG_NOTIFICATION_SETTINGS(orgId)).catch(() => ({})),
        apiCall(ENDPOINTS.CATEGORIES_BY_ORG(orgId)).catch(() => ({ results: [] })),
      ]);
      if (!alive) return;
      const s = s0 || {};
      const allowSms = s.sms_allowed !== false;
      setSmsAllowed(allowSms);
      setPackageName(s.package_name || s.package || '');
      setLimitsLocked(!!s.limits_locked);
      setCanEditProvisioning(!!s.can_edit_provisioning);
      setMaxCategories(s.max_categories != null ? String(s.max_categories) : '');
      setMaxConfigUsers(s.max_config_users != null ? String(s.max_config_users) : '');
      setUsage({
        categories: s.category_count || 0, users: s.config_user_count || 0,
        effCats: s.effective_max_categories, effUsers: s.effective_max_config_users,
      });
      setEnabled(!!s.notifications_enabled);
      setViaSms(allowSms && s.notify_via_sms !== false);
      setViaEmail(!!s.notify_via_email);
      setRemDays((s.notification_reminder_days || []).join(', '));
      const lim = s.notification_limits || {};
      const en = s.notification_types_enabled || {};
      const keys = s.notification_groups || GROUP_ORDER;
      const g = {};
      keys.forEach((k) => { g[k] = { enabled: en[k] !== false, cap: lim[k] != null ? String(lim[k]) : '' }; });
      setGroups(g);
      setCats(((c && c.results) || []).map((x) => ({ id: x.id, name: x.name || `Category #${x.id}`, positions: (x.notify_queue_positions || []).join(', ') })));
      setLoading(false);
    }, 0);
    return () => { alive = false; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, refreshKey]);

  const parseInts = (str) => String(str || '').split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isInteger(n) && n >= 0);
  const setGroup = (k, patch) => setGroups((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));

  const saveOrg = async () => {
    setSaving(true); setMsg('');
    const limits = {};
    const typesEnabled = {};
    Object.entries(groups).forEach(([k, v]) => {
      typesEnabled[k] = !!v.enabled;
      if (v.cap !== '') limits[k] = Math.max(0, parseInt(v.cap, 10) || 0);
    });
    try {
      const body = {
        notifications_enabled: enabled, notify_via_sms: smsAllowed && viaSms, notify_via_email: viaEmail,
        notification_reminder_days: parseInts(remDays),
        notification_types_enabled: typesEnabled,
      };
      // Don't send caps when locked (org admin on a free trial) — the API would 403.
      if (!limitsLocked) body.notification_limits = limits;
      // sqip-only provisioning limits ('' -> null = fall back to the plan default).
      if (canEditProvisioning) {
        body.max_categories = maxCategories === '' ? null : Math.max(0, parseInt(maxCategories, 10) || 0);
        body.max_config_users = maxConfigUsers === '' ? null : Math.max(0, parseInt(maxConfigUsers, 10) || 0);
      }
      await apiCall(ENDPOINTS.ORG_NOTIFICATION_SETTINGS(orgId), {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setMsg('Notification settings saved.');
    } catch (e) { setMsg(e.message || 'Failed to save settings.'); }
    setSaving(false);
  };

  const saveCat = async (cat) => {
    setMsg('');
    try {
      await apiCall(ENDPOINTS.CATEGORY_UPDATE_INFO(cat.id), {
        method: 'PATCH',
        body: JSON.stringify({ notify_queue_positions: parseInts(cat.positions).filter((n) => n >= 1) }),
      });
      setMsg(`Saved queue alerts for ${cat.name}.`);
    } catch (e) { setMsg(e.message || 'Failed to save category.'); }
  };

  const groupKeys = Object.keys(groups).length ? Object.keys(groups) : GROUP_ORDER;

  return (
    <Paper elevation={0} sx={cardSx}>
      {loading ? <Skeleton height={140} /> : (
        <>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            <FormControlLabel control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />} label="Enabled" />
            <Tooltip
              title={smsAllowed ? '' : `SMS isn't included in the ${packageName || 'Starter'} plan. Upgrade to enable SMS notifications.`}
              disableHoverListener={smsAllowed}
            >
              <FormControlLabel
                control={<Switch checked={smsAllowed && viaSms} onChange={(e) => setViaSms(e.target.checked)} disabled={!enabled || !smsAllowed} />}
                label="SMS"
              />
            </Tooltip>
            <FormControlLabel control={<Switch checked={viaEmail} onChange={(e) => setViaEmail(e.target.checked)} disabled={!enabled} />} label="Email" />
          </Stack>
          <Typography variant="caption" color="text.secondary">Master switch + channels (the parents). No messages are sent while Enabled is off, or on a channel that's off.</Typography>
          {!smsAllowed && (
            <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
              SMS notifications aren't included in the <strong>{packageName || 'Starter'}</strong> plan — only email is available.
              Upgrade your plan to send SMS.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Reminder days before appointment" placeholder="e.g. 1, 7"
                value={remDays} onChange={(e) => setRemDays(e.target.value)} helperText="Comma-separated; empty = no reminders" />
            </Grid>
          </Grid>

          {/* Provisioning limits — sqip staff only. Blank = use the plan default. */}
          {canEditProvisioning && (
            <>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>Plan limits (sqip only)</Typography>
              <Typography variant="caption" color="text.secondary">Per-org overrides. Leave blank to use the plan default. Blank/0 caps are treated as the plan default.</Typography>
              <Grid container spacing={2} sx={{ mt: 0.25 }}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Max categories"
                    value={maxCategories} onChange={(e) => setMaxCategories(e.target.value)}
                    helperText={`Using ${usage.categories}/${usage.effCats == null ? '∞' : usage.effCats}${maxCategories === '' ? ' (plan default)' : ''}`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Max admin users"
                    value={maxConfigUsers} onChange={(e) => setMaxConfigUsers(e.target.value)}
                    helperText={`Using ${usage.users}/${usage.effUsers == null ? '∞' : usage.effUsers}${maxConfigUsers === '' ? ' (plan default)' : ''}`} />
                </Grid>
              </Grid>
            </>
          )}

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>By notification type</Typography>
          <Typography variant="caption" color="text.secondary">Turn each type on/off and set a monthly cap (blank = unlimited). Applies on top of the channel switches above.</Typography>
          {limitsLocked && (
            <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
              Monthly caps are managed by sqip during your free trial. You can still turn types on/off.
            </Alert>
          )}
          <Stack spacing={1} sx={{ mt: 1 }} divider={<Divider flexItem />}>
            {groupKeys.map((k) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <FormControlLabel
                  sx={{ minWidth: 170, m: 0 }}
                  control={<Switch size="small" checked={groups[k]?.enabled !== false} onChange={(e) => setGroup(k, { enabled: e.target.checked })} disabled={!enabled} />}
                  label={<Typography variant="body2" fontWeight={600}>{groupLabel(k)}</Typography>}
                />
                <TextField size="small" type="number" label="Monthly cap" sx={{ maxWidth: 150 }}
                  value={groups[k]?.cap ?? ''} onChange={(e) => setGroup(k, { cap: e.target.value })}
                  disabled={!enabled || groups[k]?.enabled === false || limitsLocked} />
              </Box>
            ))}
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={saveOrg} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Save notification settings'}</Button>
          </Box>

          {cats.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Per-category queue-position alerts</Typography>
              <Typography variant="caption" color="text.secondary">Positions that text a waiting customer, e.g. 5, 3, 1. Empty = off.</Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {cats.map((c) => (
                  <Box key={c.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 600 }}>{c.name}</Typography>
                    <TextField size="small" placeholder="5, 3, 1" value={c.positions}
                      onChange={(e) => setCats((cs) => cs.map((x) => (x.id === c.id ? { ...x, positions: e.target.value } : x)))}
                      sx={{ maxWidth: 180 }} />
                    <Button size="small" onClick={() => saveCat(c)}>Save</Button>
                  </Box>
                ))}
              </Stack>
            </>
          )}

          {msg && <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>{msg}</Alert>}
        </>
      )}
    </Paper>
  );
}
